import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { TaskApi, TaskDTO } from './TaskApi';
import { TaskStatusOptions, TASK_PRIORITIES } from '../../../constants/taskConstants';
import { TaskCommentsList } from '../../taskComments/TaskCommentsList'; // ✅ Import the component

export type FormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: FormMode;
}

export const TaskForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
 
  const {
    projectId = '',
    
    projectName = '',
     
    contractId = '',
    parentId = '',
    title: parentTitle = '',
    description: parentDescription = '',
    levelNumber
  } = (location.state || {}) as {
    projectId?: string;
   
    projectName?: string;
     
    contractId?: string;
    parentId?: string;
    title?: string;
    description?: string;
    levelNumber?: string;
  };
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user?.tenantId || '';
  const tenantName = user?.tenantName || '';
  const userId = user?.id || '';
  const userName = user?.name || '';

  const [task, setTask] = useState<TaskDTO>({
    id: '',
    tenantId: tenantId,
    contractId: '',
    projectId: '',
    parentId: '',
    hierarchyPath: '',
    title: '',
    description: '',
    estimatedQuantity: 0,
    units: '',
    estimatedRate: 0,
    taxPercentage: 14,
    actualQuantity: 0,
    actualRate: 0,
    estimatedEffortHours: 0,
    actualEffortHours: 0,
    status: 'DRAFT',
    taskType: '',
    priority: '',
    givenStartDate: '',
    givenEndDate: '',
    actualStartDate: '',
    actualEndDate: '',
    progress: 0,
    isMilestone: false,
    isBillable: true,
    isDeleted: false,
    assignedTo: '',
    completedBy: '',
    approvedBy: '',
    approvalDate: '',
    isApproved: false,
    rejectionReason: '',
    reviewRequestedBy: '',
    reviewRequestedAt: '',
    rejectedBy: '',
    rejectedAt: '',
    terminationReason: '',
    comment: '',
    createdBy: '',
    updatedBy: '',
    createdAt: '',
    updatedAt: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isReadOnly = mode === 'view';

  useEffect(() => {
    if ((mode === 'view' || mode === 'edit') && id) {
      loadTaskById(id);
    } else if (mode === 'create') {
      setTask((prev) => ({
        ...prev,
        tenantId,
        projectId,
        projectName,
        tenantName,
        taxPercentage: 14,
        contractId,
        parentId,
      }));
    }
  }, [id, mode]);

  const loadTaskById = async (taskId: string) => {
    setLoading(true);
    try {
      const response = await TaskApi.getById(taskId);
      setTask(response.data);
    } catch (err) {
      setError('Failed to fetch task');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const updatedValue =
      type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setTask((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const createTask = {
        ...task,
        tenantId,
        projectId,
        projectName,
        tenantName,
        contractId,
        parentId,
      };

      const updatedTask = {
        ...task,
        tenantId,
        tenantName,
        
      };
      if (mode === 'edit' && id) {
        await TaskApi.update(id, updatedTask);
        alert('Task updated successfully!');
      } else if (mode === 'create') {
        await TaskApi.create(createTask);
        alert('Task created successfully!');
        navigate(-1);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save task. Please try again.');
    }
  };

  const handleReset = () => {
    setTask((prev) => ({
      ...prev,
      title: '',
      description: '',
      estimatedQuantity: 0,
      estimatedRate: 0,
      estimatedEffortHours: 0,
      actualQuantity: 0,
      actualRate: 0,
      actualEffortHours: 0,
      progress: 0,
    }));
  };

  const renderInput = (
    label: string,
    field: keyof TaskDTO,
    type: 'text' | 'number' | 'date' | 'checkbox' = 'text'
  ) => {
    const isCheckbox = type === 'checkbox';

    return (
      <div>
        <label className="block text-sm font-semibold mb-1">{label}</label>
        <input
          type={type}
          name={field}
          checked={isCheckbox ? Boolean(task[field]) : undefined}
          value={!isCheckbox ? (task[field] as string | number | undefined) ?? '' : undefined}
          onChange={handleChange}
          readOnly={isReadOnly && !isCheckbox}
          disabled={isReadOnly && isCheckbox}
          className={`border px-3 py-2 rounded w-full ${
            isReadOnly ? 'bg-gray-100' : ''
          }`}
        />
      </div>
    );
  };

  const renderStatusDropdown = () => (
    <div>
      <label className="block text-sm font-semibold mb-1">Status</label>
      <select
        name="status"
        value={task.status}
        onChange={handleChange}
        disabled={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${
          isReadOnly ? 'bg-gray-100' : ''
        }`}
      >
        {TaskStatusOptions.map((status) => (
          <option key={status.value} value={status.value}>
            {status.label}
          </option>
        ))}
      </select>
    </div>
  );

  const renderPriorityDropdown = () => (
    <div>
      <label className="block text-sm font-semibold mb-1">Priority</label>
      <select
        name="priority"
        value={task.priority}
        onChange={handleChange}
        disabled={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${
          isReadOnly ? 'bg-gray-100' : ''
        }`}
      >
        <option value="">Select Priority</option>
        {TASK_PRIORITIES.map((priority) => (
          <option key={priority.value} value={priority.value}>
            {priority.label}
          </option>
        ))}
      </select>
    </div>
  );

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 mx-auto bg-white w-full shadow rounded max-w-7xl">
      {error && <div className="mb-4 text-red-600">{error}</div>}

      {/* Parent Task Section */}
      {parentId && (
        <div className="border p-4 mb-6 bg-gray-50 rounded">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Parent Task</h3>
          {parentTitle && (
            <div className="mb-1">
              <span className="font-medium">Title: </span>
              <span>{parentTitle}</span>
            </div>
          )}
          {parentDescription && (
            <div>
              <span className="font-medium">Description: </span>
              <span>{parentDescription}</span>
            </div>
          )}
          {levelNumber && (
            <div>
              <span className="font-medium">Level Number: </span>
              <span>{levelNumber}</span>
            </div>
          )}
        </div>
      )}

      {/* Task Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold mb-1">Tenant Name</label>
            <input
              type="text"
              value={tenantName}
              readOnly
              className="border px-3 py-2 rounded w-full bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Project Name</label>
            <input
              type="text"
              value={projectName}
              readOnly
              className="border px-3 py-2 rounded w-full bg-gray-100"
            />
          </div>

          {renderInput('Title', 'title')}
          {renderInput('Description', 'description')}
          {renderInput('Task Type', 'taskType')}
          {renderPriorityDropdown()}
          {renderStatusDropdown()}
          {renderInput('Estimated Quantity', 'estimatedQuantity', 'number')}
          {renderInput('Units', 'units')}
          {renderInput('Estimated Rate', 'estimatedRate', 'number')}
          {renderInput('Tax (%)', 'taxPercentage', 'number')}
          {renderInput('Estimated Effort Hours', 'estimatedEffortHours', 'number')}
          {renderInput('Actual Quantity', 'actualQuantity', 'number')}
          {renderInput('Actual Rate', 'actualRate', 'number')}
          {renderInput('Actual Effort Hours', 'actualEffortHours', 'number')}
          {renderInput('Progress (%)', 'progress', 'number')}
          {renderInput('Given Start Date', 'givenStartDate', 'date')}
          {renderInput('Given End Date', 'givenEndDate', 'date')}
          {renderInput('Actual Start Date', 'actualStartDate', 'date')}
          {renderInput('Actual End Date', 'actualEndDate', 'date')}
          {renderInput('Milestone?', 'isMilestone', 'checkbox')}
          {renderInput('Billable?', 'isBillable', 'checkbox')}
          {renderInput('Approved?', 'isApproved', 'checkbox')}
        </div>

        <div className="mt-6 flex gap-4">
          {mode !== 'view' && (
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {mode === 'create' ? 'Create' : 'Update'}
            </button>
          )}
          {mode === 'create' && (
            <button
              type="button"
              onClick={handleReset}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Back
          </button>
        </div>
      </form>

      {/* ✅ Task Comments Section */}
      {/* {task.id && mode === 'edit' && (
        <div className="mt-10">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Task Comments</h2>
            <button
              onClick={() => navigate(`/project-tasks/${task.id}/comments/create`)}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              + Add Comment
            </button>
          </div>
          <TaskCommentsList taskId={task.id} />
        </div>
      )} */}
    </div>
  );
};